import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { planQuery } from '@/api/passes'
import { useCompanyCaller } from '@/app/company'
import { NothingForRole } from '@/app/nothing-for-role'
import { PlanEditor } from '@/app/passes/plan-editor'
import { canManagePlans } from '@/domain/pass-abilities'
import { draftFromPlan, PRESETS, presetDraft, type PresetKey } from '@/domain/pass-describe'
import { errorText } from '@/domain/errors'
import { t } from '@/i18n'
import { InlineBanner } from '@/ui/inline-banner'
import { Page } from '@/ui/page'
import { LoadingBlock } from '@/ui/spinner'

type Search = { from?: string; preset?: PresetKey }

export const Route = createFileRoute('/_app/c/$orgId/passes/plans/new')({
  /// `?from=<planId>` — «Дублювати»: the constructor opens on a copy (a GET
  /// and a POST in the panel, no endpoint). `?preset=<key>` — a template.
  validateSearch: (search: Record<string, unknown>): Search => ({
    ...(typeof search['from'] === 'string' ? { from: search['from'] } : {}),
    ...(typeof search['preset'] === 'string' && (PRESETS as readonly string[]).includes(search['preset'])
      ? { preset: search['preset'] as PresetKey }
      : {}),
  }),
  component: NewPlan,
})

function NewPlan() {
  const { orgId } = Route.useParams()
  const { from, preset } = Route.useSearch()
  const { caller } = useCompanyCaller(orgId)
  const source = useQuery({ ...planQuery(orgId, from ?? ''), enabled: Boolean(from) })

  if (!canManagePlans(caller)) return <NothingForRole />
  if (from && source.isPending) return <LoadingBlock label={t.app.loading} />
  if (from && source.isError) {
    return (
      <Page>
        <InlineBanner kind="error" message={errorText(source.error)} />
      </Page>
    )
  }

  const start = source.data
    ? draftFromPlan(source.data, { copy: true })
    : preset
      ? presetDraft(preset, null)
      : null
  return <PlanEditor key={from ?? preset ?? 'blank'} orgId={orgId} caller={caller} mode={{ kind: 'new', start }} />
}
